import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { atlassianApiGateway, clientVersion } from '@trello/config';

/**
 * This is a hard limit on the batch API.
 */
const MAX_URLS_PER_BATCH = 50;

/**
 * This function calls the object-resolver API and attempts to resolve all the
 * provided URLs.
 * Trello only calls the batch API.
 *
 * @param resourceUrls an array of URLs to resolve
 */
async function resolveResourceUrls(resourceUrls: string[]) {
  return fetch(
    `${atlassianApiGateway}gateway/api/object-resolver/resolve/batch`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      body: JSON.stringify(
        resourceUrls.map((resourceUrl) => ({ resourceUrl })),
      ),
    },
  );
}

async function getAvailableProducts() {
  let supportedSites: string[] = [];

  try {
    const response = await fetch(
      `${atlassianApiGateway}gateway/api/worklens/api/available-products`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Trello-Client-Version': clientVersion,
        },
      },
    );

    const data = await response.json();
    supportedSites = data.sites.map((site: { url: string }) => site.url);
  } catch (err) {
    // Ignore
  }

  return supportedSites;
}

interface ResolveResponse {
  meta: object;
  data: {
    name?: string;
    url?: string;
    generator?: {
      name: string;
      icon: {
        url: string;
      };
    };
  };
}

interface BatchResolveResponse {
  status: number;
  body: ResolveResponse;
}

interface ResolveResponseCacheEntry {
  timestamp: number;
  response: ResolveResponse | null;
}

interface QueuedFetch {
  url: string;
  resolve: (response: ResolveResponse) => void;
  reject: () => void;
}

interface ObjectResolverPattern {
  flags: string;
  source: string;
  regex: RegExp;
}

interface ObjectResolverProvider {
  key: string;
  patterns: Array<ObjectResolverPattern>;
}

function trackUrlResolvedEvent({
  resolvedUrl,
  cached,
  start,
}: {
  resolvedUrl: ResolveResponse;
  cached: boolean;
  start: number;
}) {
  Analytics.sendOperationalEvent({
    action: 'resolved',
    actionSubject: 'url',
    actionSubjectId: 'objectResolverClient',
    source: getScreenFromUrl(),
    attributes: {
      timing: Date.now() - start,
      cached,
      urlType: resolvedUrl.data.generator?.name,
    },
  });
}

interface Options {
  // Allows seeding providers for testing
  providers?: Promise<ObjectResolverProvider[]>;
}

export class ObjectResolverClient {
  private isEnabled: boolean;
  private providers: Promise<ObjectResolverProvider[]>;
  private availableSites: Promise<string[]>;
  private resolveCache: Map<string, ResolveResponseCacheEntry>;
  // A list of URLs to resolve with the next scheduled fetch
  private fetchQueue: QueuedFetch[];
  private nextFetch?: NodeJS.Timeout;
  // Time to debounce fetches while `resolveUrl` is called
  private DEBOUNCE_FETCH_MS = 100;
  // Time to keep resolved URLs in the cache, note the cache also caches
  // negative hits (nulls)
  private RESOLVE_CACHE_EXPIRY_MS = 15 * 60 * 1000; // 15 Mins

  constructor({ providers }: Options = {}) {
    this.providers = providers || this.getProviders();
    this.resolveCache = new Map();
    this.fetchQueue = [];
    this.isEnabled = true;
  }

  private async getProviders() {
    try {
      const response = await fetch(
        `${atlassianApiGateway}gateway/api/object-resolver/providers`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Trello-Client-Version': clientVersion,
          },
        },
      );
      if (response.status === 200) {
        const { providers } = await response.json();
        // Pre-compile the regexes, so matching is faster
        return providers.map((provider: ObjectResolverProvider) => {
          return {
            key: provider.key,
            patterns: provider.patterns.map((pattern) => {
              return {
                flags: pattern.flags,
                source: pattern.source,
                regex: new RegExp(pattern.source, pattern.flags),
              };
            }),
          };
        });
      }
      // Disable the object-resolver if we can't fetch providers, maybe the
      // account is not Aa, doesn't have a active session, or the service is
      // down.
      this.isEnabled = false;
      return [];
    } catch (err) {
      // Some provider regexes use named capture groups, which certain browsers
      // do not support. In this case, we fall back to worklens/available-products
      return [];
    }
  }

  async isURLSupported(url: string) {
    const providers = await this.providers;
    if (providers?.length) {
      // We have named capture group support, use the providers
      return providers.some((provider) =>
        provider.patterns.some(({ regex }) => regex?.test(url)),
      );
    }
    // We do not have named capture group support, grab the available sites,
    // and match on those
    if (!this.availableSites) {
      this.availableSites = getAvailableProducts();
    }
    return (await this.availableSites).some((site) => url.includes(site));
  }

  async _runFetch() {
    const fetchQueue = [...this.fetchQueue];
    try {
      this.fetchQueue = [];
      this.nextFetch = undefined;
      // The fetchQueue may contain fetches for the same URL multiple times
      // This can happen if the same URL is for example in a card comment
      // and also attached to a card (multiple places want to unfurl the same
      // thing). We only want to send the URL to the object-resolver service
      // once though, so dedupe fetchQueue, while ensuring we still resolve
      // all the promises.
      const urlToQueuedFetches = new Map<string, QueuedFetch[]>();
      for (const queuedFetch of fetchQueue) {
        const queuedFetches = urlToQueuedFetches.get(queuedFetch.url);
        if (queuedFetches) {
          queuedFetches.push(queuedFetch);
        } else {
          urlToQueuedFetches.set(queuedFetch.url, [queuedFetch]);
        }
      }

      // We're using a Map - order is important here, Maps preserve
      // insertion order, the batch API returns responses in order they
      // were requested
      const uniqueUrlsToFetch = [...urlToQueuedFetches.keys()];
      const res = await resolveResourceUrls(uniqueUrlsToFetch);
      const resolveResponses: BatchResolveResponse[] = await res.json();

      // Do a sanity check on the response; we should always get back a
      // response with one entry for each url we requested to unfurl
      if (uniqueUrlsToFetch.length !== resolveResponses.length) {
        throw new Error('Unexpected response length');
      }

      let i = 0;
      urlToQueuedFetches.forEach((queuedFetches) => {
        const resolveResponse = resolveResponses[i];
        if (resolveResponse.status === 200 && resolveResponse.body) {
          queuedFetches.forEach((queuedFetch) =>
            queuedFetch.resolve(resolveResponse.body),
          );
        } else {
          queuedFetches.forEach((queuedFetch) => queuedFetch.reject());
        }
        i++;
      });
    } catch (err) {
      // This means the batch request failed for some reason - we're going
      // to empty the fetch queue, so we don't keep retrying endlessly with
      // the same URLs, in case there's a "poison pill" URL.
      this.fetchQueue = [];
      if (this.nextFetch) {
        clearTimeout(this.nextFetch);
      }
      this.nextFetch = undefined;
      fetchQueue.forEach((q) => q.reject());
    }
  }

  async scheduleFetch(url: string): Promise<ResolveResponse> {
    return new Promise((resolve, reject) => {
      if (this.nextFetch) {
        clearTimeout(this.nextFetch);
      }

      this.fetchQueue.push({ url, resolve, reject });

      if (this.fetchQueue.length < MAX_URLS_PER_BATCH) {
        // If we're not at the limit, we schedule the fetch
        this.nextFetch = setTimeout(
          () => this._runFetch(),
          this.DEBOUNCE_FETCH_MS,
        );
      } else {
        // If we're at the limit we immediately run the fetch to flush the queue
        this._runFetch();
      }
    });
  }

  private async _resolveUrl(url: string) {
    if (await this.isURLSupported(url)) {
      try {
        const resolvedUrl = await this.scheduleFetch(url);
        this.resolveCache.set(url, {
          response: resolvedUrl,
          timestamp: Date.now(),
        });
        return resolvedUrl;
      } catch (e) {
        this.resolveCache.set(url, { response: null, timestamp: Date.now() });
        return null;
      }
    }

    return null;
  }

  expireResolveCache(now: number) {
    this.resolveCache.forEach((entry, key) => {
      if (entry.timestamp + this.RESOLVE_CACHE_EXPIRY_MS < now) {
        this.resolveCache.delete(key);
      }
    });
  }

  async resolveUrl(url: string) {
    if (!this.isEnabled) {
      return null;
    }
    const start = Date.now();
    this.expireResolveCache(start);

    let resolvedUrl = null;
    let cached = false;
    const cachedResolve = this.resolveCache.get(url);
    if (cachedResolve) {
      resolvedUrl = cachedResolve?.response;
      cached = true;
    } else {
      resolvedUrl = await this._resolveUrl(url);
    }
    if (resolvedUrl) {
      trackUrlResolvedEvent({ resolvedUrl, cached, start });
    }
    return resolvedUrl;
  }
}
